import React, { useState } from 'react';
import { GatheringStatus } from '../../../../../types';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import ColumnWithStore from './Column';
import ViewToggle, { ViewType } from '../ViewToggle';
import TableView from '../TableView';
import MonthView from '../MonthView';
import WeekView from '../WeekView';
import GatheringDetailModal from '../../gathering-detail/GatheringDetailModal';
import NeedsMoreInfoModal from '../../modals/NeedsMoreInfoModal';
import DeniedModal from '../../modals/DeniedModal';
import FilterBar from '../../filters/FilterBar';
import { useViewState } from '../../../hooks/useViewState';
import { useFilters } from '../../../hooks/useFilters';
import gatheringDataService from '../../../../../services/data/gatheringService';
import { getGatheringById } from '../../../../../services/data/selectors';
import GatheringCreateModal from '../../gathering-detail/GatheringCreateModal';

interface BoardProps {
  token: string | null;
}

const Board: React.FC<BoardProps> = ({ token }) => {

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  // Define the order of status columns
  const statusOrder = [
    GatheringStatus.NEW,
    GatheringStatus.IN_REVIEW,
    GatheringStatus.NEED_MORE_INFO,
    GatheringStatus.RESCHEDULE,
    GatheringStatus.AWAITING_GATHERING_LINK,
    GatheringStatus.READY_TO_PUBLISH,
    GatheringStatus.PUBLISHED,
    GatheringStatus.DENIED
  ];

  // Track which columns are collapsed
  const [collapsedColumns, setCollapsedColumns] = useState<Record<GatheringStatus, boolean>>({
    [GatheringStatus.NEW]: false,
    [GatheringStatus.IN_REVIEW]: false,
    [GatheringStatus.NEED_MORE_INFO]: false,
    [GatheringStatus.RESCHEDULE]: false,
    [GatheringStatus.AWAITING_GATHERING_LINK]: false,
    [GatheringStatus.READY_TO_PUBLISH]: false,
    [GatheringStatus.PUBLISHED]: false,
    [GatheringStatus.DENIED]: false
  });

  // State for gathering detail modal
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedGatheringId, setSelectedGatheringId] = useState<string | null>(null);

  // State for Needs More Info modal
  const [isNeedsMoreInfoModalOpen, setIsNeedsMoreInfoModalOpen] = useState(false);
  const [needsMoreInfoGathering, setNeedsMoreInfoGathering] = useState<{
    id: string;
    title?: string;
    reason: GatheringStatus.NEED_MORE_INFO | GatheringStatus.RESCHEDULE;
  } | null>(null);

  // State for Denied modal
  const [isDeniedModalOpen, setIsDeniedModalOpen] = useState(false);
  const [deniedGathering, setDeniedGathering] = useState<{
    id: string;
    title?: string;
  } | null>(null);

  // State for refresh operation
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);

  // View toggle state from useViewState hook
  const { currentView, changeView } = useViewState();
  
  // Filter state from useFilters hook
  const { filters, setFilters } = useFilters();

  // Toggle column collapse state
  const toggleColumnCollapse = (status: GatheringStatus) => {
    setCollapsedColumns(prev => ({
      ...prev,
      [status]: !prev[status]
    }));
  };

  // Handle status updates (for drag-and-drop functionality)
  const handleUpdateStatus = async (gatheringId: string, newStatus: GatheringStatus) => {
    if (!token) {
      console.warn('Cannot update gathering status: No authentication token available');
      return;
    }
    
    // If the new status is either NEED_MORE_INFO or RESCHEDULE, show the modal to collect additional information
  if (newStatus === GatheringStatus.NEED_MORE_INFO || newStatus === GatheringStatus.RESCHEDULE) {
      // Get the gathering title if available
      const gathering = getGatheringById(gatheringId);
      setNeedsMoreInfoGathering({
        id: gatheringId,
        title: gathering?.title,
        reason: newStatus 
      });
      setIsNeedsMoreInfoModalOpen(true);
      return;
    }

    // If the new status is DENIED, show the modal to collect denial reason
    if (newStatus === GatheringStatus.DENIED) {
      // Get the gathering title if available
      const gathering = getGatheringById(gatheringId);
      setDeniedGathering({
        id: gatheringId,
        title: gathering?.title
      });
      setIsDeniedModalOpen(true);
      return;
    }
    
    try {
      // Use the optimistic update method from the gathering service
      await gatheringDataService.updateGatheringStatus(gatheringId, newStatus);
    } catch (error) {
      console.error(`Error moving gathering ${gatheringId} to ${newStatus}:`, error);
      // Error handling is already done in the service with rollback
    }
  };

  // Handler for opening gathering detail modal
  const handleOpenGatheringDetail = (gatheringId: string) => {
    setSelectedGatheringId(gatheringId);
    setIsDetailModalOpen(true);
  };

  // Handler for closing gathering detail modal
  const handleCloseGatheringDetail = () => {
    setIsDetailModalOpen(false);
  };
  
  // Handler for closing the Needs More Info modal
  const handleCloseNeedsMoreInfoModal = () => {
    setIsNeedsMoreInfoModalOpen(false);
    setNeedsMoreInfoGathering(null);
  };
  
  // Handler for closing the Denied modal
  const handleCloseDeniedModal = () => {
    setIsDeniedModalOpen(false);
    setDeniedGathering(null);
  };
  
  // Handler for refreshing all data
  const handleRefreshData = async () => {
    if (!token || isRefreshing) return;
    
    setIsRefreshing(true);
    try {
      await gatheringDataService.refreshAllData();
    } catch (error) {
      console.error('Error refreshing data:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="board">
        <div className="board-header constrained-width">
          <h2>Gatherings</h2>
          
          <div className="board-controls">
            <button 
              className="refresh-button"
              onClick={handleRefreshData}
              disabled={isRefreshing}
            >
              {isRefreshing ? 'Refreshing...' : 'Refresh Data'}
            </button>

            <button className="refresh-button" style={{ marginLeft: "16px" }} onClick={() => setIsCreateModalOpen(true)} >Create Gathering </button>
            
            {isCreateModalOpen && token && (
              <GatheringCreateModal 
                isOpen={isCreateModalOpen} 
                onClose={() => setIsCreateModalOpen(false)} 
                token={token} 
                onCreated={handleRefreshData}
                onCreatedWithId={(newId) => {
                  setSelectedGatheringId(newId);
                  setIsDetailModalOpen(true);
                }}
              />
            )}


            <ViewToggle 
              currentView={currentView}
              onViewChange={changeView}
            />
          </div>
        </div>
        
        {/* Add FilterBar component */}
        <FilterBar 
          token={token} 
          filters={filters} 
          setFilters={setFilters} 
          enableDateFilter={currentView === ViewType.BOARD || currentView === ViewType.TABLE}
          enableSearchFilter={currentView === ViewType.BOARD || currentView === ViewType.TABLE}
        />
          
        {/* Conditionally render Board, Table, or Calendar view */}
        {currentView === ViewType.BOARD ? (
          <div className="board-content-wrapper">
            <div className="board-content">
              {/* Render all columns using ColumnWithStore */}
              {statusOrder.map(status => (
                <ColumnWithStore
                  key={status}
                  status={status}
                  token={token}
                  filters={filters}
                  onUpdateStatus={handleUpdateStatus}
                  collapsed={collapsedColumns[status]}
                  onToggleCollapse={() => toggleColumnCollapse(status)}
                  onViewDetails={handleOpenGatheringDetail}
                />
              ))}
            </div>
          </div>
        ) : currentView === ViewType.TABLE ? (
          <div className="constrained-width">
            <TableView 
              token={token}
              filters={filters}
              onViewDetails={handleOpenGatheringDetail}
            />
          </div>
        ) : (
          <div className="constrained-width">
            {currentView === ViewType.MONTH_VIEW ? (
              <MonthView
                token={token}
                filters={filters}
                onViewDetails={handleOpenGatheringDetail}
              />
            ) : (
              <WeekView 
                token={token}
                filters={filters}
                onViewDetails={handleOpenGatheringDetail}
              />
            )}
          </div>
        )}
        
        {/* Gathering Detail Modal */}
        {isDetailModalOpen && selectedGatheringId && token && (
          <GatheringDetailModal
            gatheringId={selectedGatheringId}
            isOpen={isDetailModalOpen}
            onClose={handleCloseGatheringDetail}
            token={token}
          />
        )}
        
        {/* Needs More Info Modal */}
        {isNeedsMoreInfoModalOpen && needsMoreInfoGathering && token && (
          <NeedsMoreInfoModal
            gatheringId={needsMoreInfoGathering.id}
            isOpen={isNeedsMoreInfoModalOpen}
            onClose={handleCloseNeedsMoreInfoModal}
            onSuccess={handleRefreshData}
            token={token}
            gatheringTitle={needsMoreInfoGathering.title}
            status={needsMoreInfoGathering.reason === GatheringStatus.RESCHEDULE
            ? GatheringStatus.RESCHEDULE
            : GatheringStatus.NEED_MORE_INFO}
          />
        )}

        {/* Denied Modal */}
        {isDeniedModalOpen && deniedGathering && token && (
          <DeniedModal
            gatheringId={deniedGathering.id}
            isOpen={isDeniedModalOpen}
            onClose={handleCloseDeniedModal}
            onSuccess={handleRefreshData}
            token={token}
            gatheringTitle={deniedGathering.title}
          />
        )}
      </div>
    </DndProvider>
  );
};

export default Board;